// frontend/src/pages/Articles.js
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, Eye, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { ARTICLES, CATEGORIES } from '../data/ArticlesData';
// import { AdInline } from '../components/AdBanner';

// ── Category pill style ───────────────────────────────────────
const CAT_STYLES = {
  'PDF Tools':   { bg: 'rgba(255,45,75,0.12)',   color: '#ff6b7a' },
  'Image Tools': { bg: 'rgba(59,130,246,0.12)',  color: '#60a5fa' },
  'AI Tools':    { bg: 'rgba(168,85,247,0.12)',  color: '#c084fc' },
};

function catStyle(cat) {
  return CAT_STYLES[cat] || { bg: 'rgba(255,255,255,0.06)', color: '#8b93b8' };
}

// ── Featured hero card ────────────────────────────────────────
function FeaturedCard({ article }) {
  const cs = catStyle(article.category);
  return (
    <Link to={`/articles/${article.slug}`} style={{ textDecoration: 'none', display: 'block' }}>
      <div style={{
        background: 'var(--glass-1)', border: '1px solid var(--border)',
        borderRadius: 'var(--r-xl)', overflow: 'hidden', backdropFilter: 'blur(12px)',
        display: 'grid', gridTemplateColumns: '1fr 1.1fr', minHeight: 300,
        transition: 'all 0.28s cubic-bezier(0.34,1.56,0.64,1)',
        position: 'relative',
      }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = article.accentColor + '55'; e.currentTarget.style.boxShadow = `0 24px 56px rgba(0,0,0,0.45), 0 0 0 1px ${article.accentColor}33`; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.boxShadow = ''; }}>

        {/* Left: emoji cover */}
        <div style={{ background: article.coverColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 96, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 40%, rgba(255,255,255,0.07), transparent 70%)' }} />
          <span style={{ position: 'absolute', top: 16, left: 16, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>Featured</span>
          {article.icon}
        </div>

        {/* Right: content */}
        <div style={{ padding: '36px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: cs.bg, color: cs.color }}>{article.category}</span>
            <span style={{ fontSize: '0.72rem', color: 'var(--t-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={11} />{article.readTime}</span>
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.2rem,2.5vw,1.6rem)', fontWeight: 800, color: 'var(--t-primary)', lineHeight: 1.2, letterSpacing: '-0.025em', marginBottom: 14 }}>{article.title}</h2>
          <p style={{ fontSize: '0.88rem', color: 'var(--t-secondary)', lineHeight: 1.65, marginBottom: 22 }}>{article.excerpt}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: article.accentColor, fontSize: '0.85rem', fontWeight: 700 }}>
            Read article <ArrowRight size={15} />
          </div>
        </div>
      </div>
    </Link>
  );
}

// ── Standard article card ─────────────────────────────────────
function ArticleCard({ article }) {
  const cs = catStyle(article.category);
  return (
    <Link to={`/articles/${article.slug}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      <div style={{
        background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)',
        overflow: 'hidden', backdropFilter: 'blur(8px)', height: '100%', display: 'flex', flexDirection: 'column',
        transition: 'all 0.28s cubic-bezier(0.34,1.56,0.64,1)',
      }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.borderColor = article.accentColor + '44'; e.currentTarget.style.boxShadow = `0 16px 40px rgba(0,0,0,0.4), 0 0 0 1px ${article.accentColor}22`; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.boxShadow = ''; }}>

        {/* Cover */}
        <div style={{ height: 140, background: article.coverColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 52, flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 40%, rgba(255,255,255,0.06), transparent 70%)' }} />
          {article.icon}
        </div>

        {/* Body */}
        <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: cs.bg, color: cs.color }}>{article.category}</span>
            <span style={{ fontSize: '0.7rem', color: 'var(--t-tertiary)', display: 'flex', alignItems: 'center', gap: 3 }}><Clock size={10} />{article.readTime}</span>
          </div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '0.97rem', fontWeight: 700, color: 'var(--t-primary)', marginBottom: 8, lineHeight: 1.3, letterSpacing: '-0.01em' }}>{article.title}</h3>
          <p style={{ fontSize: '0.8rem', color: 'var(--t-secondary)', lineHeight: 1.55, flex: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{article.excerpt}</p>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            {/* <span style={{ fontSize: '0.72rem', color: 'var(--t-muted)', fontFamily: 'var(--font-mono)' }}>
              {new Date(article.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
            </span>
            <span style={{ fontSize: '0.72rem', color: 'var(--t-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Eye size={11} />{article.views.toLocaleString()}
            </span> */}
          </div>
        </div>
      </div>
    </Link>
  );
}

// ══════════════════════════════════════════════════════════════
//  MAIN ARTICLES PAGE
// ══════════════════════════════════════════════════════════════
export default function Articles() {
  const [search,   setSearch]   = useState('');
  const [category, setCategory] = useState('');
  const [visible,  setVisible]  = useState(9);

  const filtered = useMemo(() => ARTICLES.filter(a => {
    const matchCat = !category || a.category === category;
    const q = search.toLowerCase();
    const matchQ = !q || a.title.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q) || a.tags.some(t => t.includes(q));
    return matchCat && matchQ;
  }), [search, category]);

  const featured = (!search && !category) ? filtered[0] : null;
  const grid     = (!search && !category) ? filtered.slice(1, visible + 1) : filtered.slice(0, visible);

  return (
    <div>
       <Helmet>
        <title>Articles — ILoveConvert | Tool Guides & Tutorials</title>
        <meta name="description" content="In-depth how-to guides for every ILoveConvert tool — PDF conversion, image editing, background removal and more. Free guides for students and professionals." />
        <meta name="keywords" content="pdf guides, image conversion guides, how to convert pdf, pdf tool tutorials, file conversion guides" />
        <link rel="canonical" href="https://iloveconvert.in/articles" />
        <meta property="og:title" content="Articles — ILoveConvert | Tool Guides & Tutorials" />
        <meta property="og:url" content="https://iloveconvert.in/articles" />
        <meta property="og:description" content="In-depth how-to guides for every ILoveConvert tool." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Articles — ILoveConvert | Tool Guides & Tutorials" />
        <meta name="twitter:description" content="In-depth how-to guides for every ILoveConvert tool." />
      </Helmet>
      
      {/* ── HERO ────────────────────────────────────────────── */}
      <section className="hero" style={{ padding: '72px 28px 56px' }}>
        <div className="hero-inner">
          <h1 style={{ fontSize: 'clamp(2rem,5vw,3.4rem)' }}>
            How-To <span className="accent">Articles</span>
          </h1>
          <p>In-depth guides for every tool — PDF conversion, image editing, background removal, and more.</p>

          {/* Search */}
          <div style={{ position: 'relative', maxWidth: 520, width: '100%', margin: '0 auto' }}>
            <Search size={16} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--t-tertiary)', pointerEvents: 'none' }} />
            <input value={search} onChange={e => { setSearch(e.target.value); setVisible(9); }} placeholder="Search articles…"
              style={{ width: '100%', padding: '13px 16px 13px 44px', background: 'var(--glass-2)', border: '1px solid var(--border-mid)', borderRadius: 'var(--r-lg)', color: 'var(--t-primary)', fontFamily: 'var(--font-body)', fontSize: '0.95rem', outline: 'none', backdropFilter: 'blur(12px)' }}
              onFocus={e => e.target.style.borderColor = 'var(--red)'}
              onBlur={e => e.target.style.borderColor = 'var(--border-mid)'} />
          </div>
        </div>
      </section>

      <div className="container" style={{ padding: '0 28px 80px' }}>
        {/* Category filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 40, alignItems: 'center' }}>
          {['All', ...CATEGORIES].map(cat => {
            const active = cat === 'All' ? !category : category === cat;
            return (
              <button key={cat} onClick={() => { setCategory(cat === 'All' ? '' : cat); setVisible(9); }}
                style={{ padding: '8px 18px', borderRadius: 50, border: `1px solid ${active ? 'var(--red)' : 'var(--border)'}`, background: active ? 'rgba(255,45,75,0.12)' : 'var(--glass-1)', color: active ? 'var(--red)' : 'var(--t-secondary)', cursor: 'pointer', fontWeight: 600, fontSize: '0.84rem', fontFamily: 'var(--font-body)', transition: 'all 0.2s', backdropFilter: 'blur(8px)' }}>
                {cat}
              </button>
            );
          })}
          <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--t-tertiary)', fontFamily: 'var(--font-mono)' }}>
            {filtered.length} article{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--t-secondary)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
            <p>No articles match your search. Try a different keyword.</p>
          </div>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <div style={{ marginBottom: 28 }}>
                <FeaturedCard article={featured} />
              </div>
            )}

            {/* <AdInline /> */}

            {/* Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 20, marginTop: 8 }}>
              {grid.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {/* Show More */}
            {visible < filtered.length - (featured ? 1 : 0) && (
              <div style={{ textAlign: 'center', marginTop: 48 }}>
                <button onClick={() => setVisible(v => v + 6)} className="btn btn-outline"
                  style={{ padding: '12px 32px', fontSize: '0.95rem' }}>
                  Show More Articles →
                </button>
                <p style={{ fontSize: '0.78rem', color: 'var(--t-tertiary)', marginTop: 10, fontFamily: 'var(--font-mono)' }}>
                  Showing {Math.min(visible, filtered.length - (featured ? 1 : 0))} of {filtered.length - (featured ? 1 : 0)} articles
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* <AdInline /> */}

      {/* SEO section */}
      <section className="section" style={{ background: 'var(--ink-1)', paddingTop: 0 }}>
        <div className="container">
          <div className="seo-content">
            <p className="seo-section-label">About these guides</p>
            <h2 className="seo-h2">Expert How-To Guides for Every ILoveConvert Tool</h2>
            <p className="seo-lead">Each article is written to help you get the most out of our file conversion tools — from understanding the basics to mastering advanced techniques. All guides are regularly updated to reflect the latest tool features and best practices.</p>
            <div className="seo-grid-3">
              {[
                { t: 'PDF Guides', d: 'Converting, merging, splitting, compressing and securing PDF files.' },
                { t: 'Image Guides', d: 'Format conversion, resizing, WebP optimisation and background removal.' },
                { t: 'AI Tools', d: 'Background removal with AI — how it works and how to get perfect results.' },
              ].map(c => (
                <div key={c.t} className="seo-card">
                  <div className="seo-card-title">{c.t}</div>
                  <div className="seo-card-text">{c.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
